import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { signUp, getWorkspaces, getWorkspaceInvites, clearUnauthorized, clearError } from "../../../actions";
import moment from "moment-timezone";
import { validateEmail, validateName, validatePassword } from "../../../js/validation";
import { AuthInput } from "../../../src-new/components/inputs/AuthInput";
import { FormattedMessage, useIntl } from "react-intl";

const SignUpForm = (props) => {
  const { dark } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const userToBeVerified = new URLSearchParams(location.search).get("email");
  const [fullName, setFullName] = useState("");
  const [emailAddress, setEmailAddress] = useState(userToBeVerified || "");
  const [password, setPassword] = useState("");
  const [passwordCopy, setPasswordCopy] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordCopyVisible, setPasswordCopyVisible] = useState(false);
  const [fullNameError, setFullNameError] = useState("");
  const [emailAddressError, setEmailAddressError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordCopyError, setPasswordCopyError] = useState("");
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitHandler = async (e) => {
    e.preventDefault();
    let hasError = false;

    if (!fullName) { setFullNameError(intl.formatMessage({ id: 'NAME_BLANK', defaultMessage: 'Name cannot be blank' })); hasError = true; }
    if (!emailAddress) { setEmailAddressError(intl.formatMessage({ id: 'EMAIL_BLANK', defaultMessage: 'Email cannot be blank' })); hasError = true; }
    if (!password) { setPasswordError(intl.formatMessage({ id: 'PASSWORD_BLANK', defaultMessage: 'Password cannot be blank' })); hasError = true; }
    if (!passwordCopy) { setPasswordCopyError(intl.formatMessage({ id: 'CONFIRM_PASSWORD_BLANK', defaultMessage: 'Please confirm your password' })); hasError = true; }
    if (!isTermsChecked) { setTermsError(intl.formatMessage({ id: 'YOU_MUST_ACCEPT_TERMS_AND_CONDITIONS', defaultMessage: 'You must accept the Terms and Conditions' })); hasError = true; }
    if (hasError) return;

    validateName(fullName, intl, (nameValid, nameMsg) => {
      if (!nameValid) { setFullNameError(nameMsg); return; }
      validateEmail(emailAddress, intl, (emailValid, emailMsg) => {
        if (!emailValid) { setEmailAddressError(emailMsg); return; }
        validatePassword(password, intl, (passValid, passMsg) => {
          if (!passValid) { setPasswordError(passMsg); return; }
          if (password !== passwordCopy) {
            setPasswordCopyError(intl.formatMessage({ id: 'PASSWORDS_DONT_MATCH', defaultMessage: "Passwords don't match" }));
            return;
          }
          setIsSubmitting(true);
          dispatch(signUp({
            fullName,
            emailAddress,
            password,
            timezones: [{ name: moment.tz.guess(), isPrimary: true }],
          }))
            .then(async () => {
              props.setEmail(emailAddress);
              await dispatch(getWorkspaces("SignUpForm"));
              await dispatch(getWorkspaceInvites());
              dispatch(clearUnauthorized());
              dispatch(clearError());
              navigate("/setup-account", { replace: true });
            })
            .catch((err) => {
              setIsSubmitting(false);
              console.error(err);
            });
        });
      });
    });
  };

  return (
    <form onSubmit={submitHandler} noValidate>
      <AuthInput dark={dark} label={intl.formatMessage({ id: 'FULL_NAME', defaultMessage: 'Full name' })} name="fullName" value={fullName} onChange={(e) => { setFullName(e.target.value); setFullNameError(''); }} placeholder="John Doe" error={fullNameError} />
      <AuthInput dark={dark} label={intl.formatMessage({ id: 'EMAIL_ADDRESS', defaultMessage: 'Email address' })} name="email" value={emailAddress} onChange={(e) => { setEmailAddress(e.target.value); setEmailAddressError(''); }} placeholder="you@company.com" error={emailAddressError} />
      <AuthInput dark={dark} label={intl.formatMessage({ id: 'PASSWORD', defaultMessage: 'Password' })} isPassword type="password" value={password} onChange={(e) => { setPassword(e.target.value); setPasswordError(''); if (passwordCopy) setPasswordCopyError(e.target.value === passwordCopy ? '' : intl.formatMessage({ id: 'PASSWORDS_DONT_MATCH', defaultMessage: "Passwords don't match" })); }} placeholder="8+ characters" error={passwordError} passwordVisible={passwordVisible} togglePasswordVisibility={() => setPasswordVisible(v => !v)} />
      <AuthInput dark={dark} label={intl.formatMessage({ id: 'CONFIRM_PASSWORD', defaultMessage: 'Confirm password' })} isPassword type="password" value={passwordCopy} onChange={(e) => { setPasswordCopy(e.target.value); setPasswordCopyError(!e.target.value ? intl.formatMessage({ id: 'CONFIRM_PASSWORD_BLANK', defaultMessage: 'Please confirm your password' }) : e.target.value !== password ? intl.formatMessage({ id: 'PASSWORDS_DONT_MATCH', defaultMessage: "Passwords don't match" }) : ''); }} placeholder="Re-enter password" error={passwordCopyError} passwordVisible={passwordCopyVisible} togglePasswordVisibility={() => setPasswordCopyVisible(v => !v)} />

      <div className="mt-5 flex flex-col gap-1">
        <label className="flex items-start gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isTermsChecked}
            onChange={(e) => { setIsTermsChecked(e.target.checked); setTermsError(''); }}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 flex-shrink-0"
          />
          <span className="text-sm leading-snug" style={dark ? { color: 'rgba(255,255,255,0.5)' } : { color: '#4b5563' }}>
            <FormattedMessage
              id="ACCEPT_TERMS_AND_CONDITIONS"
              defaultMessage="I accept Workflo's <link>Terms and Conditions</link>"
              values={{
                link: (msg) => (
                  <span
                    className="hover:underline cursor-pointer"
                    style={dark ? { color: 'rgba(147,197,253,0.9)' } : { color: '#2563eb' }}
                    onClick={() => window.open('https://workflo.com/terms-and-conditions/', '_blank')}
                  >
                    {msg}
                  </span>
                )
              }}
            />
          </span>
        </label>
        {termsError && <p className="text-xs text-red-400 ml-6">{termsError}</p>}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className={`mt-6 w-full py-2.5 px-4 text-white text-sm font-semibold rounded-xl shadow-sm ${dark ? 'auth-btn-primary' : 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 transition-colors duration-150'}`}
      >
        {isSubmitting
          ? <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Creating account…
            </span>
          : <FormattedMessage id="SIGN_UP" defaultMessage="Create account" />
        }
      </button>

      <p className="mt-5 text-center text-sm" style={dark ? { color: 'rgba(255,255,255,0.4)' } : { color: '#6b7280' }}>
        <FormattedMessage id="HAVE_ACCOUNT" defaultMessage="Already have an account?" />{' '}
        <Link to="/signin" className="font-medium" style={dark ? { color: 'rgba(147,197,253,0.9)' } : { color: '#2563eb' }}>
          <FormattedMessage id="LOG_IN" defaultMessage="Log in" />
        </Link>
      </p>
    </form>
  );
};

export default SignUpForm;
